import { useMemo } from "react";
import type { TypedDocumentNode } from "urql";
import { gql, useQuery } from "urql";
import { useLocale } from "@acto/web-common/locales";
import { ProductPeerDrilldownValueKind } from "@acto/gql/graphql";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { formatCurrency } from "@acto/taskpane/field-sales-2/utils/format";

export type PeerPotentialsValueKind = "AVG" | "RANGE" | "RANKED_ONLY";

export interface PeerPotentialsRange {
  min: number;
  max: number;
}

export interface PeerPotentialsItem {
  productId: string;
  productName: string | null;
  hasChildren: boolean;
  peerBuyers: number;
  peerAdoptionPercentage: number;
  kind: PeerPotentialsValueKind;
  avgValue: number | null;
  range: PeerPotentialsRange | null;
  /** The right-aligned figure: avg, q25–q75 band, or null when ranked without a number. */
  formattedValue: string | null;
}

export interface PeerPotentialsNode {
  productName: string | null;
  level: number;
}

export interface ProductPeerDrilldown {
  node: PeerPotentialsNode;
  /** Level of the returned children (the node's level + 1). */
  depth: number;
  /** Denominator for peerAdoptionPercentage; distinct cohort clients with orders in the last 12m. */
  peerCohortSize: number;
  rows: PeerPotentialsItem[];
}

export interface PeerPotentialsResult {
  isPending: boolean;
  error: Error | undefined;
  data: ProductPeerDrilldown | null;
  refetch: () => void;
}

interface ProductPeerDrilldownQueryData {
  productPeerDrilldown: {
    node: { productName: string | null; level: number };
    depth: number;
    peerCohortSize: number;
    items: {
      id: string;
      productName: string | null;
      hasChildren: boolean;
      peerBuyers: number;
      peerAdoptionPercentage: number;
      kind: ProductPeerDrilldownValueKind;
      avgValue: number | null;
      range: { min: number; max: number } | null;
    }[];
  } | null;
}

interface ProductPeerDrilldownQueryVariables {
  clientId: string;
  productIds: string[];
  level: number;
}

const ProductPeerDrilldownQuery: TypedDocumentNode<ProductPeerDrilldownQueryData, ProductPeerDrilldownQueryVariables> =
  gql`
    query GetProductPeerDrilldown($clientId: ID!, $productIds: [ID!]!, $level: Int!) {
      productPeerDrilldown(clientId: $clientId, productIds: $productIds, level: $level) {
        node {
          productName
          level
        }
        depth
        peerCohortSize
        items {
          id
          productName
          hasChildren
          peerBuyers
          peerAdoptionPercentage
          kind
          avgValue
          range {
            min
            max
          }
        }
      }
    }
  `;

// A listed product has at least one peer buyer, so a share below one percent is real, not zero.
const formatPercentage = (value: number, locale: string): string =>
  value > 0 && value < 1 ? "<1" : new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

function toPeerDrilldownValue(
  kind: ProductPeerDrilldownValueKind,
  avgValue: number | null,
  range: { min: number; max: number } | null,
  currency: ReturnType<typeof useCurrencyContext>,
  locale: string,
): string | null {
  switch (kind) {
    case ProductPeerDrilldownValueKind.Avg:
      return avgValue != null ? formatCurrency(avgValue, currency, locale) : null;
    case ProductPeerDrilldownValueKind.Range:
      return range != null
        ? `${formatCurrency(range.min, currency, locale)} – ${formatCurrency(range.max, currency, locale)}`
        : null;
    case ProductPeerDrilldownValueKind.RankedOnly:
      return null;
    default:
      return null;
  }
}

export function usePeerPotentials(
  clientId: string | undefined,
  productIds: string[] | undefined,
  level: number | undefined,
): PeerPotentialsResult {
  const { locale } = useLocale();
  const currency = useCurrencyContext();

  const [result, executeQuery] = useQuery<ProductPeerDrilldownQueryData, ProductPeerDrilldownQueryVariables>({
    query: ProductPeerDrilldownQuery,
    variables: {
      clientId: clientId ?? "",
      productIds: productIds ?? [],
      level: level ?? 0,
    },
    requestPolicy: "cache-first",
    pause: !clientId || !productIds?.length || level == null,
  });

  const data = useMemo<ProductPeerDrilldown | null>(() => {
    const drilldown = result.data?.productPeerDrilldown;
    if (!drilldown) return null;

    return {
      node: drilldown.node,
      depth: drilldown.depth,
      peerCohortSize: drilldown.peerCohortSize,
      rows: drilldown.items.map((item) => ({
        productId: item.id,
        productName: item.productName,
        hasChildren: item.hasChildren,
        peerBuyers: item.peerBuyers,
        peerAdoptionPercentage: item.peerAdoptionPercentage,
        kind: item.kind,
        avgValue: item.avgValue,
        range: item.range,
        formattedValue: toPeerDrilldownValue(item.kind, item.avgValue, item.range, currency, locale),
      })),
    };
  }, [result.data, currency, locale]);

  return {
    isPending: result.fetching || result.stale,
    error: result.error,
    data,
    refetch: () => executeQuery({ requestPolicy: "network-only" }),
  };
}

export { formatPercentage };
